// Выделение прямоугольника и копирование его в буфер обмена.
//
// Это ровно та половина работы, за которую покупные сетки берут от 999 долларов на
// разработчика (`ПРАВИЛА.md`, решение 4): выделение диапазона и обмен с буфером. Вторая
// половина, вставка, уже живёт рядом в `paste.ts` — и обе стороны обязаны понимать друг друга:
// человек копирует кусок ведомости, правит его в Excel и вставляет обратно.
//
// Разделитель — знак табуляции, конец строки — перевод строки. Так кладёт в буфер Excel, так же
// он и читает; менять формат нельзя, даже если он выглядит бедно.

import type { GridColumn, GridCursor } from './types';

/** Прямоугольник выделения. Границы упорядочены: `from` всегда не больше `to`. */
export interface GridRange {
  readonly fromRow: number;
  readonly toRow: number;
  readonly fromColumn: number;
  readonly toColumn: number;
}

/**
 * Упорядочить углы выделения.
 *
 * Человек тянет выделение в любую сторону, в том числе снизу вверх и справа налево: якорь
 * оказывается больше каретки. Дальше по сетке ходят только упорядоченные границы, иначе обход
 * пустой и «скопировалось ничего» — без единого сообщения.
 */
export function normalizeRange(anchor: GridCursor, focus: GridCursor): GridRange {
  return {
    fromRow: Math.min(anchor.rowIndex, focus.rowIndex),
    toRow: Math.max(anchor.rowIndex, focus.rowIndex),
    fromColumn: Math.min(anchor.columnIndex, focus.columnIndex),
    toColumn: Math.max(anchor.columnIndex, focus.columnIndex),
  };
}

/** Попадает ли ячейка в выделение. */
export function inRange(range: GridRange, rowIndex: number, columnIndex: number): boolean {
  return (
    rowIndex >= range.fromRow &&
    rowIndex <= range.toRow &&
    columnIndex >= range.fromColumn &&
    columnIndex <= range.toColumn
  );
}

/** Сколько ячеек в выделении. Единица — выделения как такового нет, стоит одна каретка. */
export function rangeSize(range: GridRange): number {
  return (range.toRow - range.fromRow + 1) * (range.toColumn - range.fromColumn + 1);
}

/**
 * Собрать выделенное в текст для буфера обмена.
 *
 * Берётся `raw`, а не показанное: в буфер обязано уехать значение, а не оформление. «85 000,00»
 * с неразрывными пробелами Excel читает как текст и в сумму не берёт, а «85000.00» — как число.
 * Показанный текст остаётся запасным вариантом для колонок без сырого значения (состояние
 * строки, отметка, подпись).
 *
 * Колонок, которых нет в наборе, здесь не бывает: каталог уже отфильтрован по шаблону объекта,
 * и скрытая колонка не попадает ни в разметку, ни сюда.
 */
export function copyText<TRow>({
  rows,
  columns,
  range,
}: {
  rows: readonly TRow[];
  columns: readonly GridColumn<TRow>[];
  range: GridRange;
}): string {
  const строки: string[] = [];
  for (let номерСтроки = range.fromRow; номерСтроки <= range.toRow; номерСтроки += 1) {
    const строка = rows[номерСтроки];
    if (строка === undefined) {
      continue;
    }
    const ячейки: string[] = [];
    for (let номерКолонки = range.fromColumn; номерКолонки <= range.toColumn; номерКолонки += 1) {
      const колонка = columns[номерКолонки];
      if (колонка === undefined) {
        continue;
      }
      const состояние = колонка.cell(строка);
      ячейки.push(экранировать(состояние.raw ?? состояние.text));
    }
    строки.push(ячейки.join('\t'));
  }
  return строки.join('\n');
}

/**
 * Значение со знаком табуляции или переводом строки — в кавычках, как это делает Excel.
 *
 * Без этого одна заметка с переводом строки развалила бы прямоугольник: дальше в буфере всё
 * съезжает на колонку и на строку, а человек увидит это уже вставленным в свой файл.
 */
function экранировать(значение: string): string {
  if (!/[\t\n\r"]/.test(значение)) {
    return значение;
  }
  return `"${значение.replace(/"/g, '""')}"`;
}
